import { set, add, sub, format, type FormatOptions } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { type Customer } from '@/lib/serializers'

// #region Shift definition

export type ShiftDefinition = {
  hours: number
  minutes?: number
}

export type ShiftsDefinition = {
  timeZone: string
  shifts: ShiftDefinition[]
}

export const defaultShiftsDefinition: ShiftsDefinition = {
  timeZone: 'Etc/UTC',
  shifts: [{ hours: 6 }, { hours: 14 }, { hours: 22 }],
}

export const setDefaultShiftsDefinition = (
  timeZone: string,
  dayStartHourArg: number | string,
): ShiftsDefinition => {
  const dayStartHour = Number.parseInt(`${dayStartHourArg}`, 10)

  const newShiftsDefinition = {
    timeZone,
    shifts: [
      { hours: dayStartHour % 24 },
      { hours: (dayStartHour + 8) % 24 },
      { hours: (dayStartHour + 16) % 24 },
    ].sort((a, b) => a.hours - b.hours),
  }

  const theSame = JSON.stringify(defaultShiftsDefinition) === JSON.stringify(newShiftsDefinition)

  if (!theSame) {
    defaultShiftsDefinition.timeZone = newShiftsDefinition.timeZone
    defaultShiftsDefinition.shifts = newShiftsDefinition.shifts
  }

  return defaultShiftsDefinition
}

export const setDefaultShiftsDefinitionFromDocument = () => {
  if (typeof document !== 'undefined') {
    const timeZone = document.getElementById('root')?.dataset.timezone
    const dayStartHour = document.getElementById('root')?.dataset.dayStartHour

    if (timeZone) {
      setDefaultShiftsDefinition(timeZone, dayStartHour ?? 6)
    }
  }
}

export const setDefaultShiftsDefinitionFromCustomer = (customer: Customer | null) => {
  if (customer && customer.timeZone) {
    setDefaultShiftsDefinition(customer.timeZone, customer.dayStartHour ?? 6)
  }
}

export function getShiftDefinition(
  shift: number,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): ShiftDefinition {
  const shiftIndex = shift - 1

  if (shiftIndex >= shiftsDefinition.shifts.length) {
    throw new Error(
      `Invalid shift - ${shift}. It must be a range from 1 to ${shiftsDefinition.shifts.length}.`,
    )
  }

  return shiftsDefinition.shifts[shiftIndex]
}

// #endregion
// #region Shift time helpers

export function toTimeZoneDate(
  date: Date,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): Date {
  return toZonedTime(date, shiftsDefinition.timeZone)
}

export function formatInTimeZone(
  date: Date,
  formatStr: string,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
  options?: FormatOptions,
) {
  return format(toTimeZoneDate(date, shiftsDefinition), formatStr, options)
}

export function getShift(
  date: Date,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): number {
  const dateTz = toTimeZoneDate(date, shiftsDefinition)

  for (let i = shiftsDefinition.shifts.length - 1; i >= 0; --i) {
    const shift = i + 1

    if (isAfterShift(dateTz, shift, shiftsDefinition)) {
      return shift
    }
  }

  return shiftsDefinition.shifts.length
}

export function isWithinShift(
  date: Date,
  shift: number,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): boolean {
  const dateTz = toTimeZoneDate(date, shiftsDefinition)
  const start = startOfShift(dateTz, shift, shiftsDefinition)
  const end = endOfShift(dateTz, shift, shiftsDefinition)

  return dateTz >= start && dateTz < end
}

export function isAfterShift(
  date: Date,
  shift: number,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): boolean {
  const dateTz = toTimeZoneDate(date, shiftsDefinition)
  const shiftDefinition = getShiftDefinition(shift, shiftsDefinition)

  if (shiftDefinition.hours !== dateTz.getHours()) {
    return shiftDefinition.hours <= dateTz.getHours()
  }

  if ((shiftDefinition.minutes || 0) !== dateTz.getMinutes()) {
    return (shiftDefinition.minutes || 0) <= dateTz.getMinutes()
  }

  return true
}

export function isBeforeShift(
  date: Date,
  shift: number,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): boolean {
  const dateTz = toTimeZoneDate(date, shiftsDefinition)
  const shiftDefinition = getShiftDefinition(shift, shiftsDefinition)

  if (shiftDefinition.hours !== dateTz.getHours()) {
    return shiftDefinition.hours > dateTz.getHours()
  }

  if ((shiftDefinition.minutes || 0) !== dateTz.getMinutes()) {
    return (shiftDefinition.minutes || 0) > dateTz.getMinutes()
  }

  return false
}

export function startOfShift(
  date: Date,
  shift: number,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): Date {
  const dateTz = toTimeZoneDate(date, shiftsDefinition)
  const shiftDefinition = getShiftDefinition(shift, shiftsDefinition)

  let start = set(dateTz, {
    hours: shiftDefinition.hours,
    minutes: shiftDefinition.minutes ?? 0,
    seconds: 0,
    milliseconds: 0,
  })

  if (isBeforeShift(dateTz, 1, shiftsDefinition)) {
    start = sub(start, { days: 1 })
  }

  return start
}

export function endOfShift(
  date: Date,
  shift: number,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): Date {
  const dateTz = toTimeZoneDate(date, shiftsDefinition)
  const nextShift = (shift >= shiftsDefinition.shifts.length ? 0 : shift) + 1
  const shiftDefinition = getShiftDefinition(nextShift, shiftsDefinition)

  let end = sub(
    set(dateTz, {
      hours: shiftDefinition.hours,
      minutes: shiftDefinition.minutes ?? 0,
      seconds: 0,
      milliseconds: 0,
    }),
    {
      seconds: 1,
    },
  )

  if (isBeforeShift(dateTz, 1, shiftsDefinition)) {
    end = sub(end, { days: 1 })
  }

  if (nextShift === 1) {
    end = add(end, { days: 1 })
  }

  return end
}

export function startOfCurrentShift(
  date: Date = new Date(),
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): Date {
  return startOfShift(date, getShift(date, shiftsDefinition), shiftsDefinition)
}

export function endOfCurrentShift(
  date: Date = new Date(),
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): Date {
  return endOfShift(date, getShift(date, shiftsDefinition), shiftsDefinition)
}

export function startOfWorkDay(
  date: Date,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): Date {
  const dateTz = toTimeZoneDate(date, shiftsDefinition)
  const shiftDefinition = getShiftDefinition(1, shiftsDefinition)

  let start = set(dateTz, {
    hours: shiftDefinition.hours,
    minutes: shiftDefinition.minutes ?? 0,
    seconds: 0,
    milliseconds: 0,
  })

  if (isBeforeShift(dateTz, 1, shiftsDefinition)) {
    start = sub(start, { days: 1 })
  }

  return start
}

export function endOfWorkDay(
  date: Date,
  shiftsDefinition: ShiftsDefinition = defaultShiftsDefinition,
): Date {
  const dateTz = toTimeZoneDate(date, shiftsDefinition)
  const shiftDefinition = getShiftDefinition(1, shiftsDefinition)

  let end = sub(
    add(
      set(dateTz, {
        hours: shiftDefinition.hours,
        minutes: shiftDefinition.minutes ?? 0,
        seconds: 0,
        milliseconds: 0,
      }),
      { days: 1 },
    ),
    { seconds: 1 },
  )

  if (isBeforeShift(dateTz, 1, shiftsDefinition)) {
    end = sub(end, { days: 1 })
  }

  return end
}

// #endregion
